// ABOUTME: React Query mutation hooks for creating, updating, and deleting delivery window configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  DAY_ID_TO_CODE,
  DayIdentifier,
  DeliveryWindowApiResponse,
  DeliveryWindowFormValues,
  DeliveryWindowOverrides,
} from '../types/deliveryWindow'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

/** Appends ":00" to display time "HH:mm" → API time "HH:mm:ss" */
const toApiTime = (displayTime: string): string =>
  displayTime ? `${displayTime}:00` : ''

/** Converts form values to the API request body */
const toApiRequest = (values: DeliveryWindowFormValues, overrides: DeliveryWindowOverrides) => ({
  timeZone: {
    isOverridden: overrides.timeZone,
    value: values.timeZone?.trim() || null,
  },
  businessDays: {
    isOverridden: overrides.businessDays,
    value: { activeDays: values.businessDays.map((d) => DAY_ID_TO_CODE[d]) },
  },
  operatingSchedule: {
    isOverridden: overrides.operatingSchedule,
    value: {
      standard: {
        standardDays: values.standardOperatingDays.map((d) => DAY_ID_TO_CODE[d]),
        onlineDeliveryHours: {
          start: toApiTime(values.standardHours.onlineDelivery.start),
          end: toApiTime(values.standardHours.onlineDelivery.end),
        },
        defaultDeliveryHours: {
          start: toApiTime(values.standardHours.defaultDelivery.start),
          end: toApiTime(values.standardHours.defaultDelivery.end),
        },
      },
      nonStandard: Object.entries(values.nonStandardHours)
        .filter(([dayId]) =>
          values.businessDays.includes(dayId as DayIdentifier) &&
          !values.standardOperatingDays.includes(dayId as DayIdentifier))
        .map(([dayId, hours]) => ({
        day: DAY_ID_TO_CODE[dayId as keyof typeof DAY_ID_TO_CODE],
        onlineDeliveryHours: {
          start: toApiTime(hours.onlineDelivery.start),
          end: toApiTime(hours.onlineDelivery.end),
        },
        defaultDeliveryHours: {
          start: toApiTime(hours.defaultDelivery.start),
          end: toApiTime(hours.defaultDelivery.end),
        },
      })),
    },
  },
  minDeliveryWindow: {
    isOverridden: overrides.minDeliveryWindow,
    value: values.deliveryConstraints.minDeliveryWindow,
  },
  minFixDeliveryWindow: {
    isOverridden: overrides.minFixDeliveryWindow,
    value: values.deliveryConstraints.minFixDeliveryWindow,
  },
  stepInterval: {
    isOverridden: overrides.stepInterval,
    value: values.deliveryConstraints.stepInterval,
  },
})

export const useCreateDeliveryWindow = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: DeliveryWindowFormValues; overrides: DeliveryWindowOverrides }) => {
      const res = await api.post<DeliveryWindowApiResponse>(
        `/tuc-configs/delivery-windows/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryWindowConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'deliveryWindow', countryId, businessLine] })
    },
  })
}

export const useCreateDeliveryWindowWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values, overrides }: { scope: PendingScopeRequest; values: DeliveryWindowFormValues; overrides: DeliveryWindowOverrides }) => {
      const res = await api.post<DeliveryWindowApiResponse>(
        '/tuc-configs/delivery-windows',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values, overrides))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryWindowConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'deliveryWindow', countryId, businessLine] })
    },
  })
}

export const useUpdateDeliveryWindow = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: DeliveryWindowFormValues; overrides: DeliveryWindowOverrides }) => {
      const res = await api.put<DeliveryWindowApiResponse>(
        `/tuc-configs/delivery-windows/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryWindowConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'deliveryWindow', countryId, businessLine] })
    },
  })
}

export const useDeleteDeliveryWindow = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/delivery-windows/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryWindowConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'deliveryWindow', countryId, businessLine] })
    },
  })
}
